/**
 * Hook to fetch and cache CloudFront signed cookies for CDN access
 *
 * The signed cookies grant temporary read access to all derived assets
 * within a project. Cookies are scoped to `Path=/projects/{projectId}/`
 * so multiple projects' cookies coexist in the browser simultaneously.
 *
 * The server returns `ttlSeconds` in the response body (default 600s / 10 min).
 * The hook derives its refresh interval from this value (70% of TTL) so that
 * cookies are renewed before they expire. No hardcoded timing constants.
 *
 * We use staleTime: 0 to ensure the API is always called when the project
 * changes, which triggers the Set-Cookie headers to update browser cookies.
 * The gcTime allows cached responses to be returned instantly while refetching.
 *
 * COALESCING: Multiple components (AssetThumbnail, AudioRow, etc.) mount at
 * staggered times, each triggering a refetch due to staleTime: 0. To prevent
 * duplicate POSTs, we track the last successful mint per project and skip
 * the API call if a mint occurred within MINT_COALESCE_WINDOW_MS. The browser
 * cookies from the recent mint are still valid.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { createSignedCookies } from "@/services/api/sdk.gen";
import { logger } from "@/services/logger";
import type { CloudFrontAuth } from "@/utils/derivedAssetUrls";

// Track the last minted project ID to detect project switches
let lastMintedProjectId: string | null = null;

// ============================================================================
// MINT COALESCING
// ============================================================================
// Prevents duplicate POSTs when multiple components mount within a short window.
// The browser cookies from a recent mint are still valid, so we can skip the API
// call and return the cached response.

/** How long after a successful mint to skip subsequent mints (ms) */
const MINT_COALESCE_WINDOW_MS = 5000;

/** Cached mint data per project */
interface MintRecord {
  mintedAt: number;
  data: CloudFrontAuth;
}

/** Module-level cache of recent mints per project */
const recentMints = new Map<string, MintRecord>();

/**
 * Check if we have a recent mint for this project that we can reuse.
 * Returns the cached data if valid, or null if we need to mint fresh.
 */
function getRecentMint(projectId: string): CloudFrontAuth | null {
  const record = recentMints.get(projectId);
  if (!record) return null;

  const age = Date.now() - record.mintedAt;
  if (age < MINT_COALESCE_WINDOW_MS) {
    return record.data;
  }

  // Expired, clean up
  recentMints.delete(projectId);
  return null;
}

/**
 * Record a successful mint for coalescing subsequent requests.
 */
function recordMint(projectId: string, data: CloudFrontAuth): void {
  recentMints.set(projectId, {
    mintedAt: Date.now(),
    data,
  });

  // Clean up old entries to prevent memory leaks
  // Keep only entries from the last minute
  const cutoff = Date.now() - 60000;
  for (const [id, record] of recentMints) {
    if (record.mintedAt < cutoff) {
      recentMints.delete(id);
    }
  }
}

/**
 * Reset the mint cache - for testing only.
 * @internal
 */
export function _resetMintCache(): void {
  recentMints.clear();
  lastMintedProjectId = null;
}

/**
 * Hook to fetch CloudFront signed cookies for a project
 *
 * @param projectId - The project ID to get signed cookies for
 * @returns Query result with CloudFront auth data
 */
export function useSignedCookies(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const prevProjectIdRef = useRef<string | undefined>(undefined);

  // Invalidate cache on project switch to ensure fresh cookies are minted.
  // With per-project path-scoped cookies this is less critical (cookies coexist),
  // but still ensures the browser always has fresh cookies for the active project.
  useEffect(() => {
    if (projectId && prevProjectIdRef.current !== projectId) {
      // If switching to a different project that we've visited before,
      // invalidate to force a refetch (which re-mints cookies)
      if (prevProjectIdRef.current !== undefined) {
        logger.info("Project switch detected, invalidating signed cookies", {
          from: prevProjectIdRef.current,
          to: projectId,
        });
        queryClient.invalidateQueries({
          queryKey: ["signedCookies", projectId],
        });
      }
      prevProjectIdRef.current = projectId;
    }
  }, [projectId, queryClient]);

  return useQuery({
    queryKey: ["signedCookies", projectId],
    queryFn: async (): Promise<CloudFrontAuth | null> => {
      if (!projectId) {
        return null;
      }

      // COALESCING: Check if we have a recent mint we can reuse.
      // This prevents duplicate POSTs when multiple components mount within
      // a short window. The browser cookies from the recent mint are still valid.
      const recentMint = getRecentMint(projectId);
      if (recentMint) {
        logger.debug("Reusing recent signed cookies mint", {
          projectId,
          age: Date.now() - (recentMints.get(projectId)?.mintedAt ?? 0),
        });
        return recentMint;
      }

      // Log project switches for debugging
      const isProjectSwitch =
        lastMintedProjectId !== null && lastMintedProjectId !== projectId;

      if (isProjectSwitch) {
        logger.info("Re-minting CloudFront cookies for project switch", {
          from: lastMintedProjectId,
          to: projectId,
        });
      }

      try {
        const result = await createSignedCookies({
          path: { projectId },
          throwOnError: true,
        });

        const data = result?.data?.data;

        if (!data?.domain) {
          logger.warn("Signed cookies response missing domain", {
            projectId,
            hasData: !!data,
          });
          return null;
        }

        // Track the last minted project for detecting switches
        lastMintedProjectId = projectId;

        // Cookies are set via Set-Cookie headers by the API
        // We only need the domain, expiry, and TTL for building URLs and refresh timing
        const ttl = data.ttlSeconds ?? 600;
        const authData: CloudFrontAuth = {
          domain: data.domain,
          expiresAt:
            data.expiresAt ?? new Date(Date.now() + ttl * 1000).toISOString(),
          ttlSeconds: ttl,
        };

        // Record this mint for coalescing subsequent requests
        recordMint(projectId, authData);

        return authData;
      } catch (error) {
        logger.error("Failed to fetch signed cookies", error as Error, {
          projectId,
        });
        // Return null instead of throwing - allows graceful degradation
        // Assets may still work via direct S3 URLs if CDN fails
        return null;
      }
    },
    enabled: !!projectId,
    // CRITICAL: staleTime: 0 ensures we always refetch when projectId changes
    // This is necessary because the API call's Set-Cookie headers are what
    // update the browser cookies - a cached JS response won't re-mint them.
    // Without this, returning to a previously visited project would use stale
    // cookies from the last-visited project, causing 403 errors.
    staleTime: 0,
    // Keep cached data for instant display while refetching
    gcTime: 7 * 60 * 1000,
    // Proactively refetch at ~70% of server TTL (default 600s → 420s)
    refetchInterval: (query) => {
      const ttl = query.state.data?.ttlSeconds ?? 600;
      return Math.floor(ttl * 0.7) * 1000;
    },
    // Keep refreshing even when tab is unfocused - prevents 403 on returning to tab
    refetchIntervalInBackground: true,
    // Retry a few times for transient failures
    retry: 2,
    retryDelay: 1000,
  });
}
